import { z } from "zod";
import { isInputField } from "./fields.js";
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const ISO_DATETIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/;
/** Treat "" and null as "not answered" so optional fields accept a blank box. */
const blankToUndefined = (schema) => z.preprocess((v) => (v === "" || v === null ? undefined : v), schema.optional());
/**
 * Turns a zod failure into { fieldId: message } for a 400 response or an
 * inline preview error.
 *
 * The one rewrite: a missing required answer fails as invalid_type with zod's
 * raw "Invalid input: expected string, received undefined" — a respondent
 * needs "This field is required." A present-but-wrong-typed value keeps the
 * raw message; only crafted payloads produce those.
 */
export const answerFieldErrors = (error) => {
    const out = {};
    for (const issue of error.issues) {
        const key = String(issue.path[0] ?? "");
        if (!key || out[key])
            continue;
        out[key] =
            issue.code === "invalid_type" && /received undefined$/.test(issue.message)
                ? "This field is required."
                : issue.message;
    }
    return out;
};
const choiceRef = (field, allowOther) => {
    const valid = new Set("choices" in field ? field.choices.map((c) => c.id) : []);
    return z
        .object({
        id: z.string().nullable().default(null),
        other: z.string().trim().min(1, "Enter a value.").max(300).optional(),
    })
        .strict()
        .superRefine((val, ctx) => {
        if (val.id !== null) {
            if (!valid.has(val.id)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "Unknown choice.",
                    path: ["id"],
                });
            }
            if (val.other !== undefined) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "Cannot combine a choice with an 'other' value.",
                    path: ["other"],
                });
            }
            return;
        }
        if (!allowOther) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "A choice is required.",
                path: ["id"],
            });
        }
        else if (val.other === undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Enter a value.",
                path: ["other"],
            });
        }
    });
};
const wireSchemaFor = (field) => {
    switch (field.type) {
        case "heading":
        case "paragraph":
            // Unreachable: callers filter layout blocks out first.
            return z.never();
        case "short_text":
        case "long_text": {
            let s = z.string().trim().max(field.maxLength);
            if (field.minLength !== undefined) {
                s = s.min(field.minLength, `Use at least ${field.minLength} characters.`); // prettier-ignore
            }
            return field.required
                ? s.min(Math.max(1, field.minLength ?? 1), field.minLength ? `Use at least ${field.minLength} characters.` : "This field is required.") // prettier-ignore
                : s;
        }
        case "email":
            return z.string().trim().toLowerCase().email().max(320);
        case "number": {
            let n = field.decimals === 0 ? z.number().int() : z.number();
            if (field.min !== undefined)
                n = n.min(field.min);
            if (field.max !== undefined)
                n = n.max(field.max);
            return n;
        }
        case "date": {
            const pattern = field.mode === "datetime" ? ISO_DATETIME : ISO_DATE;
            const { min, max } = field;
            // ISO strings compare correctly lexicographically, so no Date parsing —
            // which also avoids re-introducing the timezone coercion noted above.
            return z
                .string()
                .regex(pattern, "Invalid date.")
                .superRefine((v, ctx) => {
                if (min && v < min) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: "Date is too early.",
                    });
                }
                if (max && v > max) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: "Date is too late.",
                    });
                }
            });
        }
        case "rating":
            return z.number().int().min(1).max(field.max);
        case "multiple_choice":
        case "dropdown":
            return choiceRef(field, "allowOther" in field ? field.allowOther : false);
        case "checkboxes": {
            const valid = new Set(field.choices.map((c) => c.id));
            return z
                .object({
                ids: z.array(z.string()).max(field.choices.length).default([]),
                other: z.string().trim().min(1, "Enter a value.").max(300).optional(),
            })
                .strict()
                .superRefine((val, ctx) => {
                const unknown = val.ids.filter((id) => !valid.has(id));
                if (unknown.length) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: "Unknown choice.",
                        path: ["ids"],
                    });
                }
                if (new Set(val.ids).size !== val.ids.length) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: "Duplicate choice.",
                        path: ["ids"],
                    });
                }
                if (val.other !== undefined && !field.allowOther) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: "This question has no 'other' option.",
                        path: ["other"],
                    });
                }
                const total = val.ids.length + (val.other === undefined ? 0 : 1);
                if (field.minSelected !== undefined && total < field.minSelected) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `Select at least ${field.minSelected}.`,
                        path: ["ids"],
                    });
                }
                if (field.maxSelected !== undefined && total > field.maxSelected) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `Select at most ${field.maxSelected}.`,
                        path: ["ids"],
                    });
                }
                if (field.required && total === 0) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: "Select at least one.",
                        path: ["ids"],
                    });
                }
            });
        }
        case "file_upload":
            return z
                .object({
                fileIds: z
                    .array(z.string().uuid())
                    .max(field.maxFiles)
                    .default([])
                    .refine((ids) => new Set(ids).size === ids.length, "Duplicate file.")
                    .refine((ids) => !field.required || ids.length > 0, "A file is required."),
            })
                .strict();
    }
};
/**
 * Builds the validator for one published version's wire payload.
 *
 * `.strict()` is load-bearing, not stylistic: without it an attacker posts 500
 * junk keys and every CSV export, responses table and search index downstream
 * has to defend against them.
 */
export const buildAnswerSchema = (doc) => {
    const shape = {};
    for (const field of doc.fields) {
        if (!isInputField(field))
            continue;
        const schema = wireSchemaFor(field);
        shape[field.id] = field.required ? schema : blankToUndefined(schema);
    }
    return z.object(shape).strict();
};
/** Flattened answer text for the `responses.search_text` GIN index. */
export const answersToSearchText = (doc, answers) => {
    const parts = [];
    for (const field of doc.fields) {
        if (!isInputField(field))
            continue;
        const value = answers[field.id];
        if (value === undefined || value === null)
            continue;
        if (typeof value === "string" || typeof value === "number") {
            parts.push(String(value));
        }
        else if ("files" in value) {
            parts.push(...value.files.map((f) => f.name));
        }
        else {
            const labelOf = (id) => "choices" in field
                ? (field.choices.find((c) => c.id === id)?.label ?? "")
                : "";
            if ("ids" in value)
                parts.push(...value.ids.map(labelOf));
            else if (value.id)
                parts.push(labelOf(value.id));
            if (value.other)
                parts.push(value.other);
        }
    }
    return parts.filter(Boolean).join(" ").slice(0, 8000);
};
//# sourceMappingURL=answers.js.map